/**
 * CREW COMMS (docs/CREW_COMMS.md) — a launcher in the mission top bar and a drawer:
 *   producer / shoot lead  one-tap calls (flight phase + production, the likely next call
 *                          highlighted), spoken on every crew device — a manual fallback for when
 *                          public flight data lags; the system announces what it can see itself
 *   everyone               team chat
 * Live over the WebSocket via the mission store.
 */
import './comms.css';
import { CrewVoiceLifecycle,CrewVoicePanel } from './CrewVoicePanel';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import {
  BROADCAST_CALLS, broadcastCall, lastFlightCall, nextCalls, type Broadcast, type BroadcastCall, type BroadcastPreset, type CommsMessage,
  type MissionBundle,
} from '@apos/shared';
import { Button, cx, Icon, IconButton, Segmented, toast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { fmt } from '../../lib/fmt';
import { atLeast } from '../../app/labels';
import { useIsPhone } from '../../app/useMediaQuery';
import { serverNow, useServerNow } from '../../lib/clock';
import { onNewBroadcasts, useMission } from '../../state/mission';
import { useSession } from '../../state/session';

import { BROADCAST_MAX, useSendCall } from './useCall';
import { ReportButton } from '../moderation/ReportBlockDialog';

export const COMMS_MAX = 500;
export { BROADCAST_MAX, useSendCall };
const FLIGHT_CALLS = BROADCAST_CALLS.filter((c) => c.group === 'flight');
const PRODUCTION_CALLS = BROADCAST_CALLS.filter((c) => c.group === 'production');

/** "now", "40 s", "3 min", "1 h 5 min" — how long ago a call went out. */
export function sinceText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 10) return 'now';
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ${m % 60} min ago`;
}

/** Button / thread label for a stored broadcast (unknown or custom → its text). */
export function broadcastLabel(bc: Pick<Broadcast, 'preset' | 'text'>): string {
  return broadcastCall(bc.preset)?.label ?? bc.text;
}

interface CommsUi {
  open: boolean;
  /** Newest message time the user has seen, per mission. */
  seen: Record<string, number>;
  setOpen(v: boolean): void;
  markSeen(missionId: string, t: number): void;
}

const SEEN_KEY = 'apos.comms.seen.v1';
function loadSeen(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

export const useCommsUi = create<CommsUi>((set, get) => ({
  open: false,
  seen: loadSeen(),
  setOpen: (open) => set({ open }),
  markSeen: (missionId, t) => {
    if ((get().seen[missionId] ?? 0) >= t) return;
    const seen = { ...get().seen, [missionId]: t };
    set({ seen });
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    } catch {
      /* private mode */
    }
  },
}));

/** Unread = messages from others newer than the last one seen. */
export function unreadCount(comms: readonly CommsMessage[], seenT: number, myUserId: string | null): number {
  let n = 0;
  for (const c of comms) if (c.t > seenT && c.authorUserId !== myUserId) n++;
  return n;
}

/** "Reached 2/3 phones" for the sender of a broadcast (delivery is confirmed by the phones themselves). */
export function receiptSummary(bc: Pick<Broadcast, 'receipts'>): string {
  const n = bc.receipts.length;
  if (!n) return 'No crew to receive it';
  const got = bc.receipts.filter((r) => r.receivedAt != null).length;
  return `Reached ${got}/${n} phone${n === 1 ? '' : 's'}`;
}

/** Producers and the shoot lead can broadcast (server rule: producer+ or isShootLead). */
export function canBroadcast(bundle: Pick<MissionBundle, 'myRole' | 'members'>, myUserId: string | null): boolean {
  return atLeast(bundle.myRole, 'producer') || bundle.members.some((m) => m.userId === myUserId && m.isShootLead);
}

/** A heads-up shows one short line of the message; the thread has the rest. */
const HEADS_UP_MAX = 72;

/** New messages from others while the drawer is closed: one short heads-up per burst (history never toasts). */
export function newCommsHeadsUp(fresh: readonly CommsMessage[], myUserId: string | null): { title: string; body: string } | null {
  const others = fresh.filter((c) => c.authorUserId !== myUserId);
  if (!others.length) return null;
  const last = others[others.length - 1]!;
  const name = last.authorName.replace(/ \(sim\)$/, '');
  const clip = (t: string) => (t.length > HEADS_UP_MAX ? `${t.slice(0, HEADS_UP_MAX - 1).trimEnd()}…` : t);
  return others.length === 1 ? { title: `Comms · ${name}`, body: clip(last.text) } : { title: `${others.length} new crew messages`, body: clip(`${name}: ${last.text}`) };
}

export function useUnread(): number {
  const missionId = useMission((s) => s.missionId);
  const comms = useMission((s) => s.record?.comms);
  const me = useSession((s) => s.user?.id ?? null);
  const seen = useCommsUi((s) => (missionId ? (s.seen[missionId] ?? 0) : 0));
  return comms && missionId ? unreadCount(comms, seen, me) : 0;
}

/** Top-bar button with an unread badge (the drawer itself lives in <CommsHost/>). */
export function CommsLauncher({ compact }: { compact?: boolean }) {
  const open = useCommsUi((s) => s.open);
  const setOpen = useCommsUi((s) => s.setOpen);
  const unread = useUnread();
  return (
    <button
      type="button"
      className={cx('comms-launch', compact && 'comms-launch--compact', open && 'is-on')}
      onClick={() => setOpen(!open)}
      aria-label={`Crew comms${unread ? ` — ${unread} unread` : ''}`}
      title="Crew comms — calls and team chat"
      aria-expanded={open}
    >
      <Icon name="users" size={17} />
      {!compact ? <span className="comms-launch__label">Comms</span> : null}
      {unread ? <span className="comms-launch__badge num">{unread > 99 ? '99+' : unread}</span> : null}
    </button>
  );
}

/** Menu tile variant for the phone "More" sheet. */
export function CommsMenuTile({ onOpen }: { onOpen?(): void }) {
  const setOpen = useCommsUi((s) => s.setOpen);
  const unread = useUnread();
  return (
    <button
      type="button"
      className="menu-tile comms-tile"
      onClick={() => {
        onOpen?.();
        setOpen(true);
      }}
    >
      <Icon name="users" size={22} />
      <span>Comms</span>
      {unread ? <span className="comms-launch__badge num comms-tile__badge">{unread > 99 ? '99+' : unread}</span> : null}
    </button>
  );
}

/** Mounted once by the mission shell; renders the drawer while open and closes it on unmount. */
export function CommsHost() {
  const open = useCommsUi((s) => s.open);
  const setOpen = useCommsUi((s) => s.setOpen);
  useEffect(() => () => setOpen(false), [setOpen]);
  useCommsArrivals();
  // the command deck's Calls tab is on screen: the drawer opens on the thread (one call pad at a time)
  const deckCalls = open && typeof document !== 'undefined' && !!document.querySelector('[data-testid="cmd-feed"] [data-testid="callpad"]');
  return (
    <>
      <CrewVoiceLifecycle />
      {open ? <CommsDrawer onClose={() => setOpen(false)} initialTab={deckCalls ? 'thread' : undefined} /> : null}
    </>
  );
}

/**
 * Live arrivals while the shoot is open: a message from someone else toasts (the drawer being closed,
 * the phone's Comms sits under More), and each broadcast to me is confirmed as received so the sender
 * sees "Received n/m". Messages already there when the shoot opened never toast.
 */
/** A crew message older than this is history, not an arrival. */
const COMMS_HEADS_UP_MAX_AGE_MS = 2 * 60_000;

function useCommsArrivals() {
  const missionId = useMission((s) => s.missionId);
  // only the loaded record of this mission counts: its first snapshot is history, never "new"
  const ready = useMission((s) => s.status === 'ready' && !!s.record && s.record.mission.id === s.missionId);
  const comms = useMission((s) => s.record?.comms);
  const live = useMission((s) => s.bundle?.mission.status === 'live' && s.bundle.mission.archivedAt == null);
  const me = useSession((s) => s.user?.id ?? null);
  const known = useRef<{ missionId: string | null; ids: Set<string> | null }>({ missionId: null, ids: null });
  useEffect(() => {
    if (!comms || !ready) return;
    const k = known.current;
    if (k.missionId !== missionId || !k.ids) {
      known.current = { missionId, ids: new Set(comms.map((c) => c.id)) };
      return;
    }
    const fresh = comms.filter((c) => !k.ids!.has(c.id));
    for (const c of fresh) k.ids.add(c.id);
    // an ended shoot (Replay, review) has no arrivals worth a heads-up
    const ui = useCommsUi.getState();
    // only messages sent just now count: a history backfill (Replay, a reconnect resync) never toasts
    const recent = fresh.filter((c) => c.t >= serverNow() - COMMS_HEADS_UP_MAX_AGE_MS);
    const onReplay = typeof location !== 'undefined' && /\/replay(\/|$)/.test(location.pathname);
    const heads = ui.open || !live || onReplay ? null : newCommsHeadsUp(recent, me);
    if (heads) toast({ ...heads, icon: 'users', timeoutMs: 6000, action: { label: 'Open', onClick: () => useCommsUi.getState().setOpen(true) } });
  }, [comms, missionId, me, ready, live]);
  useEffect(
    () =>
      onNewBroadcasts((list) => {
        const mid = useMission.getState().missionId;
        if (!mid) return;
        for (const bc of list) if (bc.fromUserId !== me && bc.missionId === mid) void api.broadcasts.receipt(mid, bc.id).catch(() => undefined);
      }),
    [me],
  );
}

type DrawerTab = 'calls' | 'thread';

export function CommsDrawer({ onClose, initialTab }: { onClose(): void; initialTab?: DrawerTab }) {
  const bundle = useMission((s) => s.bundle);
  const comms = useMission((s) => s.record?.comms) ?? [];
  const broadcasts = useMission((s) => s.record?.broadcasts) ?? [];
  const ingest = useMission((s) => s.ingest);
  const me = useSession((s) => s.user);
  const markSeen = useCommsUi((s) => s.markSeen);
  const isPhone = useIsPhone();
  const now = useServerNow(5000);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const canPost = bundle ? bundle.mission.archivedAt == null && bundle.mission.status !== 'ended' && atLeast(bundle.myRole, 'crew') : false;
  const lead = bundle ? canPost && canBroadcast(bundle, me?.id ?? null) : false;
  const [tab, setTab] = useState<DrawerTab>(lead ? (initialTab ?? 'calls') : 'thread');
  const call = useSendCall();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const newest = comms.length ? comms[comms.length - 1]!.t : 0;
  const showThread = !lead || tab === 'thread';

  useEffect(() => {
    if (bundle && newest && showThread) markSeen(bundle.mission.id, newest);
  }, [bundle, newest, markSeen, showThread]);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comms.length, broadcasts.length, showThread]);
  useEffect(() => {
    // A dialog (Modal) open over the drawer takes Escape itself. Capture phase: this runs before the
    // Modal's handler closes it, so the check still sees the dialog.
    const on = (e: KeyboardEvent) => e.key === 'Escape' && !document.querySelector('.modal-root') && onClose();
    window.addEventListener('keydown', on, true);
    return () => window.removeEventListener('keydown', on, true);
  }, [onClose]);

  if (!bundle) return null;
  const tz = bundle.mission.timezone;
  const lastFlight = lastFlightCall(broadcasts);
  const unreadOthers = lead && tab === 'calls' ? comms.filter((c) => c.t > (useCommsUi.getState().seen[bundle.mission.id] ?? 0) && c.authorUserId !== me?.id).length : 0;

  const post = async (body: string) => {
    const msg = body.trim();
    if (!msg || busy) return false;
    setBusy(true);
    try {
      const m = await api.comms.create(bundle.mission.id, msg.slice(0, COMMS_MAX));
      ingest([{ type: 'comms', data: m }]); // show immediately; the socket echo is de-duplicated by id
      return true;
    } catch (e) {
      toast({ title: 'Message not sent', body: errorMessage(e), tone: 'alert' });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    if (await post(text)) setText('');
  };

  const broadcast = async (preset: BroadcastPreset) => {
    if (busy) return;
    if ((await call.send(preset, text)) && preset === 'CUSTOM') setText('');
  };

  // one thread: messages and broadcasts by time; consecutive messages by one author within 2 min group
  type Item = { c?: CommsMessage; bc?: Broadcast; t: number };
  const items: Item[] = [...comms.map((c): Item => ({ c, t: c.t })), ...broadcasts.map((bc): Item => ({ bc, t: bc.t }))].sort((a, b) => a.t - b.t);
  const rows = items.map((it, i) => {
    const prev = items[i - 1];
    return { ...it, cont: !!it.c && !!prev?.c && prev.c.authorUserId === it.c.authorUserId && it.c.t - prev.c.t < 120_000 };
  });
  const myLast = [...broadcasts].reverse().find((b) => b.fromUserId === me?.id) ?? null;

  return createPortal(
    <>
      {isPhone ? <div className="sheet-scrim" onClick={onClose} /> : null}
      <aside className={cx('comms', isPhone && 'comms--phone', lead && 'comms--lead')} role="dialog" aria-label="Crew comms" data-testid="comms-drawer">
        <header className="comms__head">
          <div className="comms__head-main">
            <div className="comms__title">Crew comms</div>
            <div className="comms__phase" data-testid="comms-phase">
              {lastFlight ? (
                <>
                  <span className="comms__phase-dot" aria-hidden />
                  <b>{broadcastLabel(lastFlight)}</b>
                  <span className="muted num">· {sinceText(now - lastFlight.t)}</span>
                </>
              ) : (
                <span className="muted">No flight call yet</span>
              )}
            </div>
          </div>
          <IconButton icon="close" label="Close comms" onClick={onClose} />
        </header>
        <CrewVoicePanel/>
        {lead ? (
          <div className="comms__tabs">
            <Segmented<DrawerTab>
              size="sm"
              block
              label="Comms view"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'calls', label: 'Calls', icon: 'radio' },
                { value: 'thread', label: unreadOthers ? `Thread · ${unreadOthers}` : 'Thread', icon: 'users' },
              ]}
            />
          </div>
        ) : null}
        {lead && tab === 'calls' ? (
          <CallPad
            broadcasts={broadcasts}
            busy={busy || call.busy}
            onCall={(p) => void broadcast(p)}
            footer={
              <div className="callpad__status" aria-live="polite">
                {myLast ? (
                  <div className="callpad__receipt" data-testid="callpad-last">
                    <span>
                      Sent <b>{broadcastLabel(myLast)}</b> <span className="muted num">{sinceText(now - myLast.t)}</span>
                    </span>
                    <span className="muted small num">{receiptSummary(myLast)}</span>
                  </div>
                ) : null}
              </div>
            }
            custom={
              <form
                className="callpad__custom"
                onSubmit={(e) => {
                  e.preventDefault();
                  void broadcast('CUSTOM');
                }}
              >
                <input
                  className="input"
                  value={text}
                  maxLength={BROADCAST_MAX}
                  placeholder="Custom call, e.g. Two minutes to the next pass"
                  aria-label="Custom call"
                  onChange={(e) => setText(e.target.value)}
                />
                <Button type="submit" variant="secondary" size="sm" disabled={busy || call.busy || !text.trim()} title="Spoken on every crew device">
                  Call
                </Button>
              </form>
            }
          />
        ) : (
          <>
            <div className="comms__list" ref={listRef} aria-live="polite">
              {rows.length ? (
                rows.map(({ c, bc, cont }) =>
                  bc ? (
                    <div key={bc.id} className={cx('comms-bc', bc.fromUserId === me?.id && 'is-mine', broadcastCall(bc.preset)?.group === 'flight' && 'is-flight')} data-testid="comms-broadcast">
                      <div className="comms-msg__meta">
                        <b>Call · {bc.fromName.replace(/ \(sim\)$/, '')}</b>
                        <span className="num" title={fmt.local(bc.t, tz)}>
                          {fmt.utc(bc.t)}
                        </span>
                        {bc.fromUserId && bc.fromUserId !== me?.id && bc.preset === 'CUSTOM' ? (
                          <ReportButton
                            target={{ missionId: bundle.mission.id, contentType: 'broadcast', contentId: bc.id, userId: bc.fromUserId, name: bc.fromName.replace(/ \(sim\)$/, ''), excerpt: bc.text }}
                          />
                        ) : null}
                      </div>
                      <div className="comms-bc__text">{bc.text}</div>
                      {bc.fromUserId === me?.id ? <div className="comms-bc__receipts muted small num">{receiptSummary(bc)}</div> : null}
                    </div>
                  ) : c ? (
                    <div key={c.id} className={cx('comms-msg', cont && 'is-cont', c.authorUserId === me?.id && 'is-mine')}>
                      {!cont ? (
                        <div className="comms-msg__meta">
                          <b>{c.authorName.replace(/ \(sim\)$/, '')}</b>
                          <span className="num" title={fmt.local(c.t, tz)}>
                            {fmt.utc(c.t)}
                          </span>
                        </div>
                      ) : null}
                      <div className="comms-msg__row">
                        <div className="comms-msg__text">{c.text}</div>
                        {c.authorUserId && c.authorUserId !== me?.id ? (
                          <ReportButton
                            target={{ missionId: bundle.mission.id, contentType: 'comms', contentId: c.id, userId: c.authorUserId, name: c.authorName.replace(/ \(sim\)$/, ''), excerpt: c.text }}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : null,
                )
              ) : (
                <p className="muted small comms__empty">
                  {lead
                    ? 'No messages yet. Calls you make on the Calls tab are spoken on every crew phone.'
                    : 'No messages yet. Calls from the producer show here and are spoken if spoken updates are on.'}
                </p>
              )}
            </div>
            {canPost ? (
              <form
                className="comms__compose"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
              >
                <textarea
                  ref={inputRef}
                  className="input comms__input"
                  rows={2}
                  maxLength={COMMS_MAX}
                  value={text}
                  placeholder="Message the crew…"
                  aria-label="Message"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                />
                <div className="comms__compose-row">
                  <span className="muted small num">
                    {text.length}/{COMMS_MAX}
                  </span>
                  <span className="comms__send">
                    {lead ? (
                      <Button type="button" variant="secondary" size="sm" disabled={busy || call.busy || !text.trim() || text.trim().length > BROADCAST_MAX} title={`Spoken on every crew device (up to ${BROADCAST_MAX} characters)`} onClick={() => void broadcast('CUSTOM')}>
                        Call
                      </Button>
                    ) : null}
                    <Button type="submit" variant="primary" size="sm" loading={busy} disabled={!text.trim()}>
                      Send
                    </Button>
                  </span>
                </div>
              </form>
            ) : (
              <p className="muted small comms__readonly">{bundle.mission.status === 'ended' || bundle.mission.archivedAt != null ? 'This shoot has ended. Crew messages are preserved for review.' : 'Viewers can read the channel but not post.'}</p>
            )}
          </>
        )}
      </aside>
    </>,
    document.body,
  );
}

/**
 * The producer's call pad: the likely next call(s) first and big, then every flight-phase and
 * production call one tap away. A call is spoken on every crew device with spoken updates on.
 */
export function CallPad({ broadcasts, busy, onCall, footer, custom }: { broadcasts: readonly Broadcast[]; busy: boolean; onCall(p: BroadcastPreset): void; footer?: React.ReactNode; custom?: React.ReactNode }) {
  const next = nextCalls(broadcasts);
  const last = [...broadcasts].reverse().find((b) => broadcastCall(b.preset))?.preset ?? null;
  const btn = (c: BroadcastCall, size: 'lg' | 'md' = 'md') => (
    <button
      key={c.preset}
      type="button"
      disabled={busy}
      className={cx('callpad__btn', `callpad__btn--${c.group}`, size === 'lg' && 'callpad__btn--next', next[0] === c.preset && 'is-next', last === c.preset && 'is-last')}
      aria-label={c.hint ? `${c.label} (${c.hint})` : c.label}
      onClick={() => onCall(c.preset)}
    >
      <span className="callpad__label">{c.label}</span>
      {c.hint ? <span className="callpad__hint">{c.hint}</span> : null}
    </button>
  );
  const nextCallsList = next.map((p) => broadcastCall(p)).filter((c): c is BroadcastCall => !!c);
  return (
    <div className="callpad" data-testid="callpad">
      {nextCallsList.length ? (
        <div className="callpad__next" role="group" aria-label="Likely next call">
          <span className="callpad__group-label">Next</span>
          <div className="callpad__next-row">{nextCallsList.map((c) => btn(c, 'lg'))}</div>
        </div>
      ) : null}
      <div className="callpad__group" role="group" aria-label="Flight calls">
        <span className="callpad__group-label">
          <Icon name="aircraft" size={12} /> Flight
        </span>
        <div className="callpad__grid">{FLIGHT_CALLS.map((c) => btn(c))}</div>
      </div>
      <div className="callpad__group" role="group" aria-label="Production calls">
        <span className="callpad__group-label">
          <Icon name="camera" size={12} /> Production
        </span>
        <div className="callpad__grid">{PRODUCTION_CALLS.map((c) => btn(c))}</div>
      </div>
      {custom}
      {footer}
    </div>
  );
}
